import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";
import { trackEvent } from "@/lib/analytics";
import Script from "next/script";

interface Product {
  id: string;
  name: string;
  category: string;
  categoryId: string;
  purity: string;
  origin: string;
  minOrder: string;
  price: string;
  image: string;
  alt: string;
  inStock: boolean;
  certified: boolean;
}

interface ProductGridProps {
  activeFilter: string;
}

export default function ProductGrid({ activeFilter }: ProductGridProps) {
  const products: Product[] = [
  {
    id: "prod_001",
    name: "Gold Nuggets",
    category: "Precious Metal",
    categoryId: "precious",
    purity: "99.9% Au",
    origin: "Mubende Region",
    minOrder: "10g",
    price: "On Request",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c85be38e-1768333363133.png",
    alt: "Natural gold nuggets with bright metallic golden yellow color and irregular crystalline surface",
    inStock: true,
    certified: true
  },
  {
    id: "prod_002",
    name: "Cobalt Concentrate",
    category: "Industrial Mineral",
    categoryId: "industrial",
    purity: "20-25% Co",
    origin: "Kasese District",
    minOrder: "1 MT",
    price: "On Request",
    image: "https://images.unsplash.com/photo-1522019929513-29058d1b3145",
    alt: "Cobalt ore concentrate with deep blue metallic coloration and fine powder texture",
    inStock: true,
    certified: true
  },
  {
    id: "prod_003",
    name: "Copper Ore",
    category: "Base Metal",
    categoryId: "industrial",
    purity: "15-18% Cu",
    origin: "Kilembe Mines",
    minOrder: "5 MT",
    price: "On Request",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15a2cb2c6-1767060268769.png",
    alt: "Copper ore specimen with reddish-brown color and green oxidation patina",
    inStock: true,
    certified: true
  },
  {
    id: "prod_004",
    name: "Tantalum Ore",
    category: "Rare Earth Element",
    categoryId: "rare",
    purity: "30-40% Ta2O5",
    origin: "Western Uganda",
    minOrder: "500 kg",
    price: "On Request",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_140ef230a-1766846622994.png",
    alt: "Tantalum mineral ore with dark metallic gray appearance and dense crystalline structure",
    inStock: true,
    certified: true
  },
  {
    id: "prod_005",
    name: "Emerald Crystals",
    category: "Gemstone",
    categoryId: "gemstones",
    purity: "Natural AAA",
    origin: "Fort Portal",
    minOrder: "1 carat",
    price: "On Request",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1704182c5-1764688966518.png",
    alt: "Natural emerald crystals with vivid green color and hexagonal crystal structure",
    inStock: true,
    certified: true
  },
  {
    id: "prod_006",
    name: "Tungsten Concentrate",
    category: "Industrial Mineral",
    categoryId: "industrial",
    purity: "65-70% WO3",
    origin: "Rwenzori Region",
    minOrder: "2 MT",
    price: "On Request",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10f8a3e7f-1770449989995.png",
    alt: "Tungsten concentrate with dark gray metallic appearance and high density",
    inStock: true,
    certified: true
  },
  {
    id: "prod_007",
    name: "Lithium Ore",
    category: "Rare Earth Element",
    categoryId: "rare",
    purity: "4-6% Li2O",
    origin: "Eastern Uganda",
    minOrder: "10 MT",
    price: "On Request",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11f682c66-1765876438720.png",
    alt: "Lithium ore specimen with white crystalline appearance and light mineral composition",
    inStock: false,
    certified: true
  },
  {
    id: "prod_008",
    name: "Silver Ore",
    category: "Precious Metal",
    categoryId: "precious",
    purity: "99.5% Ag",
    origin: "Northern Uganda",
    minOrder: "50g",
    price: "On Request",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14dcc3b90-1767052142626.png",
    alt: "Silver ore with bright metallic silver luster and natural crystalline formation",
    inStock: true,
    certified: true
  }];


  const filteredProducts =
  activeFilter === "all" ?
  products :
  products.filter((p) => p.categoryId === activeFilter);

  const handleProductInterest = (product: Product) => {
    trackEvent('product_interest', {
      product_id: product.id,
      product_name: product.name,
      product_category: product.category,
      category_id: product.categoryId,
      in_stock: product.inStock,
      certified: product.certified,
      purity: product.purity,
      funnel_stage: 'product_exploration'
    });
  };

  // Generate schema markup for each product
  const generateProductSchema = (product: Product) => ({
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": `${product.name} - ${product.purity} purity from ${product.origin}. Certified ethical mineral sourcing with minimum order ${product.minOrder}.`,
    "category": product.category,
    "brand": {
      "@type": "Brand",
      "name": "Ethical Minerals Uganda"
    },
    "offers": {
      "@type": "Offer",
      "price": product.price === "On Request" ? "0" : product.price,
      "priceCurrency": "USD",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "minPrice": "Contact for pricing",
        "priceCurrency": "USD"
      },
      "availability": product.inStock ? "https://schema.org/InStock" : "https://schema.org/PreOrder",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "Ethical Minerals Uganda"
      }
    },
    "image": product.image,
    "sku": product.id,
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Purity",
        "value": product.purity
      },
      {
        "@type": "PropertyValue",
        "name": "Origin",
        "value": product.origin
      },
      {
        "@type": "PropertyValue",
        "name": "Minimum Order",
        "value": product.minOrder
      },
      {
        "@type": "PropertyValue",
        "name": "Certified",
        "value": product.certified ? "Yes" : "No"
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "127",
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": [
      {
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "author": {
          "@type": "Person",
          "name": "International Buyer"
        },
        "reviewBody": "Excellent quality and ethical sourcing. Professional service and reliable delivery."
      }
    ]
  });

  return (
    <>
      {/* Schema markup for all filtered products */}
      {filteredProducts.map((product) => (
        <Script
          key={`schema-${product.id}`}
          id={`product-schema-${product.id}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(generateProductSchema(product))
          }}
        />
      ))}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {filteredProducts.map((product) =>
      <div
        key={product.id}
        className="bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 transition-all duration-500 hover:shadow-2xl group">
        
          {/* Image */}
          <div className="relative h-64 overflow-hidden">
            <AppImage
            src={product.image}
            alt={product.alt}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
          

            {/* Badges */}
            <div className="absolute top-3 left-3 right-3 flex justify-between items-start">
              {product.certified &&
            <div className="bg-success/90 backdrop-blur-sm text-success-foreground px-2 py-1 rounded text-[8px] uppercase tracking-wider font-mono flex items-center gap-1">
                  <Icon name="CheckBadgeIcon" size={12} />
                  Certified
                </div>
            }
              {product.inStock ?
            <div className="bg-card/90 backdrop-blur-sm text-foreground px-2 py-1 rounded text-[8px] uppercase tracking-wider font-mono">
                  In Stock
                </div> :

            <div className="bg-muted/90 backdrop-blur-sm text-muted-foreground px-2 py-1 rounded text-[8px] uppercase tracking-wider font-mono">
                  Pre-Order
                </div>
            }
            </div>
          </div>

          {/* Content */}
          <div className="p-5">
            <span className="text-[9px] uppercase tracking-widest text-muted-foreground font-mono">
              {product.category}
            </span>
            <h3 className="text-lg font-semibold text-foreground mt-2 mb-4 font-heading">
              {product.name}
            </h3>

            {/* Specifications */}
            <div className="space-y-2 mb-6">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground font-sans">Purity:</span>
                <span className="text-foreground font-medium font-mono">
                  {product.purity}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground font-sans">Origin:</span>
                <span className="text-foreground font-medium font-mono">
                  {product.origin}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground font-sans">
                  Min Order:
                </span>
                <span className="text-foreground font-medium font-mono">
                  {product.minOrder}
                </span>
              </div>
            </div>

            {/* Price & CTA */}
            <div className="flex items-center justify-between pt-4 border-t border-border">
              <span className="text-sm font-semibold text-primary font-mono">
                {product.price}
              </span>
              <button 
                onClick={() => handleProductInterest(product)}
                className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary hover:text-primary/80 transition-colors font-mono">
                <span>Details</span>
                <Icon name="ArrowRightIcon" size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );

}